import { AppBar, Tab, TabProps, Tabs } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/system";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { AccessEnum } from "~/api/GeneratedApi";
import { useAppSelector } from "~/redux/store/store";
import { useAccess } from "~/utils/hooks/useAccess";
import PaddedBox from "./PaddedBox";

export type TabType = {
  id: string;
  label: string;
  component: JSX.Element;
  href: string;
  requiredAccess?: AccessEnum | AccessEnum[];
  disabled?: boolean;
};

interface GenericTabProps {
  tab: string | number;
  tabs: TabType[];
  sticky?: boolean;
}

export interface TabPanelProps {
  children: JSX.Element[] | JSX.Element;
  index: string | number;
  value: string | number;
}

interface LinkTabProps {
  value: string | number;
}

export function LinkTab({
  value,
  ...other
}: LinkTabProps & TabProps<"a">): JSX.Element {
  return (
    <Tab
      component="a"
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      value={value}
      onClick={(event: React.SyntheticEvent) => {
        event.preventDefault();
      }}
      {...other}
    />
  );
}

export function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
    >
      {value === index && <PaddedBox>{children}</PaddedBox>}
    </div>
  );
}

export const useTabStyles = makeStyles((theme) => ({
  indicator: {
    "& > span": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

const GenericTab = ({ tab, tabs, sticky }: GenericTabProps): JSX.Element => {
  const history = useHistory();
  const classes = useTabStyles();
  const isMobile = useAppSelector((s) => s.general.isMobile);
  const { hasAccess } = useAccess();

  const allowedTabs = useMemo(
    () => tabs.filter((x) => !x.requiredAccess || hasAccess(x.requiredAccess)),
    [hasAccess, tabs],
  );
  return (
    <Box display="flex" flexDirection="column" height="100%" width="100%">
      <AppBar
        position={sticky ? "sticky" : "static"}
        sx={{
          maxWidth: "100vw",
          top: sticky ? "var(--header-height)" : "unset",
        }}
      >
        <Tabs
          TabIndicatorProps={{ className: classes.indicator }}
          indicatorColor="secondary"
          value={tab}
          variant={isMobile ? "scrollable" : undefined}
          scrollButtons="auto"
          onChange={(_, val) => history.push(`${val}`)}
          sx={{ maxWidth: "100%" }}
        >
          {allowedTabs.map((_tab) => (
            <LinkTab
              key={_tab.id}
              value={_tab.id}
              label={_tab.label}
              href={_tab.href}
              disabled={_tab.disabled ?? false}
            />
          ))}
        </Tabs>
      </AppBar>

      <Box maxWidth="100vw" overflow="auto" height="100%">
        {allowedTabs.map((_tab) => (
          <TabPanel key={_tab.id} value={tab} index={_tab.id}>
            {_tab.component}
          </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default GenericTab;
